<script setup lang="ts">
import { computed } from 'vue'

import { ATTRIBUTE_FILTER_PLATFORMS, SOCIAL_PLATFORM_ATTRIBUTE_OPTIONS, SOCIAL_PLATFORM_OPTIONS } from '../../../../constants'
import { SocialPlatformAttributeEnum, type SocialPlatformCriterion, type SocialPlatformFilterValue } from '../../../../types'
import CriteriaEnumSelect from '../CriteriaEnumSelect.vue'
import CriteriaFieldCell from '../CriteriaFieldCell.vue'
import CriteriaOperatorValueField from '../CriteriaOperatorValueField.vue'
import { useCriterionField } from '../useCriterionField'

const props = defineProps<{
  row: SocialPlatformCriterion
  isDisabled?: boolean
}>()
const emits = defineEmits<(e: 'update:row', row: SocialPlatformCriterion) => void>()

const operator = useCriterionField(props, emits, 'operator')
const value = useCriterionField(props, emits, 'value')

const hasAttributes = (candidate: SocialPlatformFilterValue | null): boolean =>
  candidate !== null && ATTRIBUTE_FILTER_PLATFORMS.includes(candidate)

const hasAttributeSelect = computed(() => hasAttributes(props.row.platform))

const platform = computed({
  get: () => props.row.platform,
  set: (selected: SocialPlatformFilterValue | null) => {
    const keepsAttribute = hasAttributes(selected)
    emits('update:row', {
      ...props.row,
      platform: selected,
      attribute: keepsAttribute ? props.row.attribute : SocialPlatformAttributeEnum.Existing,
      operator: keepsAttribute ? props.row.operator : null,
      value: keepsAttribute ? props.row.value : null,
    })
  },
})

const attribute = computed({
  get: () => props.row.attribute,
  set: (selected: SocialPlatformAttributeEnum | null) => {
    const next = selected ?? SocialPlatformAttributeEnum.Existing
    const isFollowers = next === SocialPlatformAttributeEnum.Followers
    emits('update:row', {
      ...props.row,
      attribute: next,
      operator: isFollowers ? props.row.operator : null,
      value: isFollowers ? props.row.value : null,
    })
  },
})
</script>

<template>
  <div class="social-platform-fields">
    <CriteriaFieldCell>
      <CriteriaEnumSelect
        v-model="platform"
        :is-disabled="isDisabled"
        :options="SOCIAL_PLATFORM_OPTIONS"
        placeholder="Platform"
        size="large"
      />
    </CriteriaFieldCell>
    <template v-if="hasAttributeSelect">
      <CriteriaFieldCell>
        <CriteriaEnumSelect
          v-model="attribute"
          :is-disabled="isDisabled"
          :options="SOCIAL_PLATFORM_ATTRIBUTE_OPTIONS"
          placeholder="Existing"
          size="large"
        />
      </CriteriaFieldCell>
      <template v-if="row.attribute === SocialPlatformAttributeEnum.Followers">
        <CriteriaOperatorValueField v-model:operator="operator" v-model:value="value" :is-disabled="isDisabled" size="large" />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.social-platform-fields {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
</style>
